import { Component, ChangeDetectionStrategy, input, output, computed } from '@angular/core';

export type AlertVariant = 'error' | 'success' | 'warning' | 'info';

/**
 * Alert — the single inline banner. Tinted background + matching `*-text`
 * foreground per variant, a Boxicons glyph, an optional heading, an optional
 * `[alert-action]` slot, and optional dismiss. Replaces the Bootstrap `.alert-*`
 * boxes and the bespoke per-feature error cards.
 *
 * ```html
 * <bodyanalytics-alert variant="error" heading="We couldn't load your results">
 *   Your data is safe — this is a connection hiccup on our end.
 *   <button alert-action (click)="retry()">Try again</button>
 * </bodyanalytics-alert>
 * ```
 *
 * The `[alert-action]` button is styled by the Alert itself — a ghost button in
 * the variant's colour. Pass a plain `<button alert-action>`; do NOT add
 * `.btn-primary` (a filled amber CTA fights the tinted field — design review 2026-06-26).
 */
@Component({
  selector: 'bodyanalytics-alert',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './alert.component.html',
  styleUrls: ['./alert.component.scss'],
  host: {
    class: 'alert',
    '[class.alert--error]': "variant() === 'error'",
    '[class.alert--success]': "variant() === 'success'",
    '[class.alert--warning]': "variant() === 'warning'",
    '[class.alert--info]': "variant() === 'info'",
    '[attr.role]': "isAssertive() ? 'alert' : 'status'",
  },
})
export class AlertComponent {
  variant = input<AlertVariant>('info');
  heading = input<string>();
  dismissible = input(false);
  dismissed = output<void>();

  private readonly icons: Record<AlertVariant, string> = {
    error: 'bx-error-circle',
    success: 'bx-check-circle',
    warning: 'bx-error',
    info: 'bx-info-circle',
  };
  protected readonly icon = computed(() => this.icons[this.variant()]);
  protected readonly isAssertive = computed(() => this.variant() === 'error' || this.variant() === 'warning');
}
